import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useAuthStore } from '@/core/stores/auth.store';
import { ActivityLogSection } from './ActivityLogSection';
import { CommentComposer } from './CommentComposer';
import { useInfiniteTaskActivity } from '../../hooks/useInfiniteTaskActivity';
import { useInfiniteTaskComments } from '../../hooks/useInfiniteTaskComments';
import { useTaskCommentMutations } from '../../hooks/useTaskCommentMutations';
import type { FeedItem, TaskCommentDto } from '../../types';

interface ReplyState {
  parentId: string;
  authorName: string;
}

interface TaskActivityPanelProps {
  taskId: string;
  onClose: () => void;
}

export function TaskActivityPanel({ taskId, onClose }: TaskActivityPanelProps) {
  const { t } = useTranslation('tasks');
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const [replyState, setReplyState] = useState<ReplyState | null>(null);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const {
    activities,
    totalCount: activityTotal,
    hasNextPage: actHasNext,
    isFetchingNextPage: actFetching,
    isLoading: actLoading,
    fetchNextPage: actFetchNext,
  } = useInfiniteTaskActivity(taskId);

  const {
    comments,
    totalCount: commentTotal,
    hasNextPage: comHasNext,
    isFetchingNextPage: comFetching,
    isLoading: comLoading,
    fetchNextPage: comFetchNext,
  } = useInfiniteTaskComments(taskId);

  const { deleteComment } = useTaskCommentMutations(taskId);

  const rootComments: TaskCommentDto[] = [];
  const repliesMap = new Map<string, TaskCommentDto[]>();
  for (const c of comments) {
    if (!c.parentId) {
      rootComments.push(c);
    } else {
      const existing = repliesMap.get(c.parentId);
      if (existing) existing.push(c);
      else repliesMap.set(c.parentId, [c]);
    }
  }

  const feedItems: FeedItem[] = [
    ...activities.map((a): FeedItem => ({ kind: 'activity', data: a })),
    ...rootComments.map(
      (c): FeedItem => ({
        kind: 'comment',
        data: c,
        replies: repliesMap.get(c.id ?? '') ?? [],
      }),
    ),
  ].sort((a, b) => new Date(a.data.createdAt).getTime() - new Date(b.data.createdAt).getTime());

  async function handleDeleteComment(commentId: string) {
    const result = await confirm({
      title: t('activity.comment.confirmDeleteTitle'),
      description: t('activity.comment.confirmDeleteDescription'),
      confirmLabel: t('activity.comment.confirmDeleteConfirm'),
      cancelLabel: t('activity.comment.confirmDeleteCancel'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    try {
      await deleteComment.mutateAsync(commentId);
    } catch {
      // handled by global error handler
    }
  }

  return (
    <>
      <aside
        className="flex h-full flex-col border-l bg-background"
        data-testid="tasks-activity-panel"
        aria-label={t('activity.panelTitle')}
      >
        <div className="flex items-center gap-1 border-b px-3 py-1.5">
          <span className="flex-1 text-sm font-medium">{t('activity.panelTitle')}</span>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={t('activity.searchAria')}
                disabled
              >
                <Search className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('activity.searchAria')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onClose}
                aria-label={t('activity.closePanel')}
                data-testid="tasks-activity-panel-close"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('activity.closePanel')}</TooltipContent>
          </Tooltip>
        </div>

        <div className="flex-1 overflow-y-auto">
          <ActivityLogSection
            items={feedItems}
            totalCount={activityTotal + commentTotal}
            hasNextPage={actHasNext || comHasNext}
            isFetchingNextPage={actFetching || comFetching}
            isLoading={actLoading || comLoading}
            onLoadMore={() => {
              if (actHasNext) actFetchNext();
              if (comHasNext) comFetchNext();
            }}
            currentUserId={currentUserId}
            onReply={(parentId, authorName) => setReplyState({ parentId, authorName })}
            onDeleteComment={handleDeleteComment}
          />
        </div>

        <CommentComposer
          taskId={taskId}
          parentId={replyState?.parentId ?? null}
          replyToAuthorName={replyState?.authorName ?? null}
          onCancelReply={() => setReplyState(null)}
        />
      </aside>

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        confirmLabel={options?.confirmLabel}
        cancelLabel={options?.cancelLabel}
        variant={options?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
